import { NotificationSourceDesktop } from '../../client';
import { StoredNotification } from '../model/StoredNotification';
import { SortedNotificationsState } from '../store/partials/Notification';

/**
 * Insert sort new notification into a sorted notification map.
 * The notification will be binary search inserted into the object map by `notification.notification.date` with descending order.
 */
export function insertNotificationIntoSortedMaps(
  state: SortedNotificationsState,
  notification: StoredNotification
): SortedNotificationsState {
  const uuid = (notification.source as NotificationSourceDesktop).identity.uuid;
  const streamId = notification.notification.stream?.id ?? null;

  const newSortedNotification = { ...state };

  // Insert notification into sorted maps
  const byId = binaryInsertNotificationSortedMap(notification, state.byId);
  const byApplication = binaryInsertNotificationSortedMap(notification, state.byApplication[uuid]);

  newSortedNotification.byId = byId;
  newSortedNotification.byApplication[uuid] = byApplication;

  if (streamId) {
    newSortedNotification.byStream[streamId] = binaryInsertNotificationSortedMap(
      notification,
      state.byStream[streamId]
    );
  }

  return newSortedNotification;
}

export function removeNotificationFromSortedMaps(
  state: SortedNotificationsState,
  notification: StoredNotification
): SortedNotificationsState {
  // Check notification is in the store if not ignore
  if (!state.byId[notification.id]) {
    return state;
  }

  delete state.byId[notification.id];
  const uuid = (notification.source as NotificationSourceDesktop).identity.uuid;
  const streamId = notification.notification.stream?.id;
  delete state.byApplication[uuid]?.[notification.id];
  if (Object.keys(state.byApplication[uuid]).length === 0) {
    delete state.byApplication[uuid];
  }
  if (streamId) {
    delete state.byStream[streamId]?.[notification.id];
    if (Object.keys(state.byStream[streamId]).length === 0) {
      delete state.byStream[streamId];
    }
  }

  return state;
}

export function binaryInsertNotificationSortedMap(
  value: StoredNotification,
  map: Record<string, StoredNotification>
): Record<string, StoredNotification> {
  const newMap = { ...map };
  delete newMap[value.id];
  const entries = Object.entries<StoredNotification>(newMap);

  // Empty list, insert and return
  if (entries.length === 0) {
    return { [value.id]: value };
  }

  // Because the map is sorted already we can check if the first element is less than the value
  // If it is we can insert the value as the head and return
  if (value.notification.date >= entries[0][1].notification.date) {
    return Object.fromEntries([[value.id, value], ...entries]);
  }

  const insertIndex = binaryArraySearch(entries, value, 0, entries.length - 1);

  const head = entries.slice(0, insertIndex);
  const tail = entries.slice(insertIndex, entries.length);

  const newEntries = [...head, [value.id, value], ...tail];

  return Object.fromEntries(newEntries);
}

const binaryArraySearch = (
  list: [id: string, nft: StoredNotification][],
  value: StoredNotification,
  start = 0,
  end = list.length - 1
): number => {
  const mid = Math.floor((start - end) / 2 + start);

  switch (true) {
    case list[mid]?.[1].notification.date === value.notification.date:
      return mid;
    case end - start === 0:
      return 0;
    case list[mid]?.[1].notification.date < value.notification.date:
      return binaryArraySearch(list, value, mid + 1, end);
    case list[mid]?.[1].notification.date > value.notification.date:
      return binaryArraySearch(list, value, start, mid);
    default:
      return 0;
  }
};
